import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCheckCircle,
  FaDoorOpen,
  FaPrint,
  FaRedo,
  FaUndo,
  FaUserPlus,
  FaUsers,
} from 'react-icons/fa';
import apiClient from '../services/apiClient';
import {
  addDays,
  formatDisplayDate,
  getNightsBetween,
  getTodayString,
  isValidStayRange,
} from '../utils/dateUtils';
import { isVisibleRoomCategory } from '../utils/roomFilters';

const formatMoney = (value) =>
  `₹ ${Number(value || 0).toLocaleString('en-IN')}`;

const emptyGuest = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  address: '',
};

const AdminWalkIn = () => {
  const navigate = useNavigate();
  const today = getTodayString();

  const [checkInDate, setCheckInDate] = useState(today);
  const [checkOutDate, setCheckOutDate] = useState(addDays(today, 1));
  const [adults, setAdults] = useState(2);
  const [kids, setKids] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [customPrices, setCustomPrices] = useState({});
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [guest, setGuest] = useState(emptyGuest);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const nights = getNightsBetween(checkInDate, checkOutDate);
  const guestCount = Number(adults) + Number(kids);

  const displayedRooms = useMemo(() => {
    if (!rooms || rooms.length === 0) return [];
    const matched = rooms.filter(
      (room) => Number(room.maxPerson) === Number(guestCount)
    );
    return matched.length > 0 ? matched : rooms;
  }, [rooms, guestCount]);

  const selectedRoom =
    displayedRooms.find((room) => room.id === selectedRoomId) ||
    displayedRooms[0] ||
    null;

  useEffect(() => {
    if (displayedRooms.length > 0) {
      setSelectedRoomId((current) =>
        displayedRooms.some((room) => room.id === current)
          ? current
          : displayedRooms[0].id
      );
    } else {
      setSelectedRoomId(null);
    }
  }, [displayedRooms]);

  const handlePriceChange = (roomId, newPrice) => {
    setCustomPrices((prev) => ({
      ...prev,
      [roomId]: newPrice,
    }));
  };

  const selectedRoomPrice = useMemo(() => {
    if (!selectedRoom) return 0;
    const customVal = customPrices[selectedRoom.id];
    if (customVal !== undefined && customVal !== '' && !isNaN(customVal)) {
      return Number(customVal);
    }
    return Number(selectedRoom.price || 0);
  }, [selectedRoom, customPrices]);

  const pricing = useMemo(() => {
    const roomPrice = selectedRoomPrice;
    const roomTotal = roomPrice * (nights || 0);
    const taxes = 0; // No GST for walk-in manual rates
    return {
      roomPrice,
      roomTotal,
      taxes,
      totalAmount: roomTotal,
    };
  }, [selectedRoomPrice, nights]);

  const loadRooms = useCallback(async () => {
    if (!isValidStayRange(checkInDate, checkOutDate)) {
      setRooms([]);
      setSelectedRoomId(null);
      setError('Check-out must be after check-in.');
      setLoadingRooms(false);
      return;
    }

    setLoadingRooms(true);
    setError('');
    try {
      const response = await apiClient.get('/inventory/available', {
        params: {
          checkIn: checkInDate,
          checkOut: checkOutDate,
          maxPerson: guestCount || 1,
        },
      });
      const available = (response.data || []).filter(isVisibleRoomCategory);
      setRooms(available);
      setCustomPrices((prev) => {
        const nextPrices = { ...prev };
        available.forEach((r) => {
          if (nextPrices[r.id] === undefined) {
            nextPrices[r.id] = r.price;
          }
        });
        return nextPrices;
      });
      setSelectedRoomId((current) =>
        available.some((room) => room.id === current)
          ? current
          : (available[0]?.id || null)
      );
    } catch (err) {
      console.error('Failed to load walk-in rooms:', err);
      setRooms([]);
      setError(err.response?.data?.message || 'Could not load available rooms.');
    } finally {
      setLoadingRooms(false);
    }
  }, [checkInDate, checkOutDate, guestCount]);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  const handleGuestChange = (event) => {
    const { name, value } = event.target;
    if (name === 'phoneNumber') {
      setGuest((prev) => ({ ...prev, phoneNumber: value.replace(/\D/g, '').slice(0, 10) }));
      return;
    }
    setGuest((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    const nextCheckIn = getTodayString();
    setCheckInDate(nextCheckIn);
    setCheckOutDate(addDays(nextCheckIn, 1));
    setAdults(2);
    setKids(0);
    setGuest(emptyGuest);
    setSelectedRoomId(null);
    setCustomPrices({});
    setSuccess(null);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    if (!guest.firstName.trim()) {
      setError('Enter the guest first name.');
      return;
    }
    if (guest.phoneNumber.length !== 10) {
      setError('Enter a valid 10-digit phone number.');
      return;
    }
    if (!selectedRoom) {
      setError('Select an available room.');
      return;
    }
    if (!Number.isFinite(pricing.totalAmount) || pricing.totalAmount <= 0) {
      setError('Enter a valid walk-in rate greater than zero.');
      return;
    }
    if (guestCount > Number(selectedRoom.maxPerson || 0)) {
      setError(`This room allows up to ${selectedRoom.maxPerson} guests.`);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await apiClient.post('/bookings/walk-in', {
        source: 'walk-in',
        checkInNow: true,
        customerDetails: {
          ...guest,
          firstName: guest.firstName.trim(),
          lastName: guest.lastName.trim(),
          address: guest.address.trim() || 'Walk-in at front desk',
        },
        roomId: selectedRoom.id,
        roomType: selectedRoom.name,
        checkInDate,
        checkOutDate,
        adults: Number(adults),
        kids: Number(kids),
        amount: pricing.totalAmount,
        payment_method: 'Walk-in Paid',
        mealPlan: 'CP',
        mealCost: 0,
      });

      const bookingId = response.data.bookingId;
      const invoiceBooking = {
        bookingId,
        customerName: `${guest.firstName.trim()} ${guest.lastName.trim()}`.trim(),
        phoneNumber: guest.phoneNumber,
        email: guest.email,
        address: guest.address.trim() || 'Walk-in at front desk',
        checkInDate,
        checkOutDate,
        roomType: selectedRoom.name,
        mealPlan: 'CP',
        adults: Number(adults),
        kids: Number(kids),
        amount: pricing.totalAmount,
      };

      setSuccess({
        bookingId,
        invoiceBooking,
        roomName: selectedRoom.name,
        checkedIn: true,
        paymentCollected: true,
      });
    } catch (err) {
      console.error('Walk-in booking failed:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openInvoice = () => {
    if (!success?.invoiceBooking) return;
    const key = success.bookingId;
    window.localStorage.setItem(`invoice:${key}`, JSON.stringify(success.invoiceBooking));
    navigate('/invoice', { state: { booking: success.invoiceBooking } });
  };

  const openGRC = () => {
    if (!success?.invoiceBooking) return;
    const key = success.bookingId;
    window.localStorage.setItem(`grc:${key}`, JSON.stringify(success.invoiceBooking));
    window.open(`/grc?key=${encodeURIComponent(key)}&print=1`, '_blank', 'noopener,noreferrer');
  };

  const inputClass =
    'w-full rounded-xl border border-gray-600 bg-gray-900/70 px-4 py-3 text-white !placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 [color-scheme:dark]';
  const labelClass = 'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-300';

  if (success) {
    return (
      <div className="min-h-screen p-6 md:p-8 bg-gradient-to-b from-gray-800 to-gray-900 text-white">
        <div className="max-w-3xl mx-auto bg-gradient-to-r from-gray-700 to-gray-800 rounded-2xl p-8 md:p-12 text-center shadow-lg">
          <FaCheckCircle className="mx-auto text-teal-400" size={64} />
          <p className="mt-6 text-xs font-semibold tracking-[0.25em] uppercase text-teal-300">Walk-in confirmed</p>
          <h2 className="mt-2 text-4xl font-extrabold">Room booked</h2>
          <p className="mt-3 text-gray-300">
            {success.invoiceBooking.customerName} is assigned to {success.roomName}.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
            <div className="rounded-xl bg-gray-900/60 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-400">Booking ID</p>
              <p className="mt-1 font-semibold break-all">{success.bookingId}</p>
            </div>
            <div className="rounded-xl bg-gray-900/60 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-400">Stay</p>
              <p className="mt-1 font-semibold">
                {formatDisplayDate(checkInDate)} — {formatDisplayDate(checkOutDate)}
              </p>
            </div>
            <div className="rounded-xl bg-gray-900/60 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-400">Amount</p>
              <p className="mt-1 font-semibold">{formatMoney(success.invoiceBooking.amount)}</p>
              <p className="text-xs text-gray-400 mt-1">
                {success.paymentCollected ? 'Payment collected' : 'Pay at hotel'}
                {success.checkedIn ? ' · Checked in' : ''}
              </p>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
            <button
              type="button"
              onClick={openGRC}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-teal-500 text-gray-900 px-5 py-3 font-bold hover:bg-teal-400 shadow-lg transition-all"
            >
              <FaPrint /> Print GRC
            </button>
            <button
              type="button"
              onClick={openInvoice}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white text-gray-900 px-5 py-3 font-semibold hover:bg-gray-100"
            >
              <FaPrint /> Print invoice
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-blue-600 px-5 py-3 font-semibold"
            >
              <FaUserPlus /> New walk-in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 bg-gradient-to-b from-gray-800 to-gray-900 text-white">
      <div className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-teal-300">Front desk</p>
          <h1 className="text-4xl font-extrabold mt-1">Walk-in Booking</h1>
          <p className="text-gray-300 mt-2 max-w-2xl">
            Assign a room the moment a guest arrives. Availability, tariff, and check-in happen in one place.
          </p>
        </div>
        <div className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-teal-500 to-blue-600 px-5 py-4">
          <FaDoorOpen size={22} />
          <div>
            <p className="text-sm text-white/80">Tonight</p>
            <p className="text-xl font-bold">{formatDisplayDate(today)}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
        <div className="space-y-6">
          <section className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold">Stay details</h2>
              <button
                type="button"
                onClick={loadRooms}
                className="inline-flex items-center gap-2 text-sm text-teal-300 hover:text-white"
              >
                <FaRedo /> Refresh rooms
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className={labelClass}>Check-in</label>
                <input
                  type="date"
                  min={today}
                  value={checkInDate}
                  onChange={(e) => {
                    const nextCheckIn = e.target.value;
                    setCheckInDate(nextCheckIn);
                    if (!isValidStayRange(nextCheckIn, checkOutDate)) {
                      setCheckOutDate(addDays(nextCheckIn, 1));
                    }
                  }}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Check-out</label>
                <input
                  type="date"
                  min={addDays(checkInDate, 1)}
                  value={checkOutDate}
                  onChange={(e) => setCheckOutDate(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Adults</label>
                <select value={adults} onChange={(e) => setAdults(Number(e.target.value))} className={inputClass}>
                  {[1, 2, 3, 4].map((count) => (
                    <option key={count} value={count}>
                      {count}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Kids</label>
                <select value={kids} onChange={(e) => setKids(Number(e.target.value))} className={inputClass}>
                  {[0, 1, 2, 3].map((count) => (
                    <option key={count} value={count}>
                      {count}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <span className="rounded-full bg-gray-900/70 px-3 py-1">{nights || 0} night{nights === 1 ? '' : 's'}</span>
              <span className="rounded-full bg-gray-900/70 px-3 py-1 inline-flex items-center gap-2">
                <FaUsers /> {guestCount} guest{guestCount === 1 ? '' : 's'}
              </span>
              <span className="rounded-full bg-gray-900/70 px-3 py-1">
                {displayedRooms.length} room{displayedRooms.length === 1 ? '' : 's'} available (Max {guestCount} guests)
              </span>
            </div>
          </section>

          <section className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-base font-semibold">Select Available Room</h2>
                <p className="text-xs text-gray-300">Showing rooms tailored for {guestCount} guest{guestCount === 1 ? '' : 's'}. Adjust tariff if needed.</p>
              </div>
              {selectedRoom && (
                <span className="rounded-full bg-teal-500/20 border border-teal-400/40 px-3 py-1 text-xs font-semibold text-teal-300">
                  Selected: {selectedRoom.name}
                </span>
              )}
            </div>

            {loadingRooms ? (
              <p className="text-gray-300 animate-pulse py-4 text-center">Checking room availability...</p>
            ) : displayedRooms.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-500 p-6 text-center text-gray-300">
                No rooms available for these dates and guest count. Try another stay or occupancy.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-600 bg-gray-900/60 shadow-inner">
                <table className="w-full text-left text-sm text-gray-200">
                  <thead className="bg-gray-800/90 text-xs font-semibold uppercase tracking-wider text-gray-400 border-b border-gray-700">
                    <tr>
                      <th className="py-2.5 px-3.5 text-center">Select</th>
                      <th className="py-2.5 px-3.5">Room Category</th>
                      <th className="py-2.5 px-3.5">Occupancy</th>
                      <th className="py-2.5 px-3.5">Standard Rate</th>
                      <th className="py-2.5 px-3.5 min-w-[160px]">Walk-in Rate (₹/night)</th>
                      <th className="py-2.5 px-3.5">Stay Total</th>
                      <th className="py-2.5 px-3.5 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {displayedRooms.map((room) => {
                      const selected = room.id === selectedRoomId;
                      const currentPrice =
                        customPrices[room.id] !== undefined ? customPrices[room.id] : room.price;
                      const numericPrice = Number(currentPrice);
                      const displayPrice =
                        currentPrice === ''
                          ? ''
                          : Number.isFinite(numericPrice)
                            ? Math.trunc(numericPrice)
                            : currentPrice;
                      const effectivePrice = Number(displayPrice || 0);
                      const totalStayPrice = effectivePrice * (nights || 0);
                      const isModified = Number(currentPrice) !== Number(room.price);

                      return (
                        <tr
                          key={room.id}
                          onClick={() => setSelectedRoomId(room.id)}
                          className={`cursor-pointer transition-colors ${
                            selected
                              ? 'bg-teal-950/60 border-l-4 border-l-teal-400'
                              : 'hover:bg-gray-800/50'
                          }`}
                        >
                          <td className="py-2.5 px-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="radio"
                              name="roomSelection"
                              checked={selected}
                              onChange={() => setSelectedRoomId(room.id)}
                              className="h-4 w-4 accent-teal-400 cursor-pointer"
                            />
                          </td>
                          <td className="py-2.5 px-3.5">
                            <div className="font-semibold text-white text-sm">{room.name}</div>
                            <div className="text-[11px] text-gray-400">ID #{room.id}</div>
                          </td>
                          <td className="py-2.5 px-3.5">
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-300">
                              <FaUsers className="text-teal-400" /> Max {room.maxPerson} guests
                            </span>
                          </td>
                          <td className="py-2.5 px-3.5 text-gray-400 text-xs font-medium">
                            {formatMoney(room.price)}
                          </td>
                          <td className="py-2.5 px-3.5" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1.5">
                              <span className="text-teal-300 font-bold text-xs">₹</span>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={displayPrice}
                                onChange={(e) => handlePriceChange(room.id, e.target.value)}
                                onFocus={() => setSelectedRoomId(room.id)}
                                className="w-24 rounded-md border border-gray-600 bg-gray-950 px-2.5 py-1 text-xs text-white font-semibold focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
                              />
                              {isModified && (
                                <button
                                  type="button"
                                  title="Reset to default rate"
                                  onClick={() => handlePriceChange(room.id, room.price)}
                                  className="text-xs text-gray-400 hover:text-white p-0.5"
                                >
                                  <FaUndo size={11} />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-2.5 px-3.5 font-bold text-sm text-teal-300">
                            {formatMoney(totalStayPrice)}
                          </td>
                          <td className="py-2.5 px-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => setSelectedRoomId(room.id)}
                              className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
                                selected
                                  ? 'bg-teal-500 text-gray-950 shadow'
                                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-600'
                              }`}
                            >
                              {selected ? 'Selected' : 'Select'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-2xl p-6 shadow-lg">
            <h2 className="text-lg font-semibold mb-5">Guest details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>First name</label>
                <input
                  name="firstName"
                  value={guest.firstName}
                  onChange={handleGuestChange}
                  className={inputClass}
                  placeholder="Rahul"
                />
              </div>
              <div>
                <label className={labelClass}>Last name</label>
                <input
                  name="lastName"
                  value={guest.lastName}
                  onChange={handleGuestChange}
                  className={inputClass}
                  placeholder="Sharma"
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  name="phoneNumber"
                  value={guest.phoneNumber}
                  onChange={handleGuestChange}
                  className={inputClass}
                  placeholder="10-digit mobile"
                  inputMode="numeric"
                  maxLength={10}
                />
              </div>
              <div>
                <label className={labelClass}>Email (optional)</label>
                <input
                  name="email"
                  type="email"
                  value={guest.email}
                  onChange={handleGuestChange}
                  className={inputClass}
                  placeholder="guest@email.com"
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Address (optional)</label>
                <input
                  name="address"
                  value={guest.address}
                  onChange={handleGuestChange}
                  className={inputClass}
                  placeholder="Street, City, State, Pincode"
                />
              </div>
            </div>
          </section>
        </div>

        <aside className="xl:sticky xl:top-4 h-fit space-y-4">
          <div className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-2xl p-6 shadow-lg space-y-4">
            <h2 className="text-lg font-semibold">Booking summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-gray-300">Room</span>
                <span className="font-medium text-right">{selectedRoom?.name || 'Not selected'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-300">Dates</span>
                <span className="font-medium text-right">
                  {formatDisplayDate(checkInDate)} → {formatDisplayDate(checkOutDate)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Room tariff</span>
                <span>{formatMoney(pricing.roomTotal)}</span>
              </div>
              <div className="border-t border-gray-600 pt-3 flex justify-between text-base font-bold">
                <span>Total Amount</span>
                <span className="text-teal-300">{formatMoney(pricing.totalAmount)}</span>
              </div>
            </div>

            {error && (
              <p className="rounded-xl bg-red-500/20 border border-red-400/40 px-3 py-2 text-sm text-red-200">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || loadingRooms}
              className="w-full rounded-xl bg-gradient-to-r from-teal-500 to-blue-600 py-3.5 font-bold text-lg disabled:opacity-60 hover:from-teal-400 hover:to-blue-500 transition-all shadow-lg"
            >
              {submitting ? 'Booking...' : 'Confirm walk-in'}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default AdminWalkIn;
